import { z } from "zod";

/** Ein Ordner in einer Vorlage, beliebig tief verschachtelt. */
export interface TemplateNode {
  name: string;
  children?: TemplateNode[];
}

export interface TemplateInfo {
  id: string;
  name: string;
  description: string;
  /** Eingebaute Vorlagen gehören HandVero und lassen sich nicht löschen. */
  builtin: boolean;
  structure: TemplateNode[];
}

const n = (name: string, ...children: string[]): TemplateNode =>
  children.length ? { name, children: children.map((c) => ({ name: c })) } : { name };

/** Ordner der Hausakte; sie wird auch beim ersten Start eines Privatkontos angelegt. */
export const HAUSAKTE_STRUCTURE: TemplateNode[] = [
  n("01_Elektro", "Verteilung und Zähler", "Geräte und Typenschilder"),
  n("02_Heizung und Warmwasser", "Wartung", "Typenschilder und Handbücher"),
  n("03_Wasser und Abwasser"),
  n("04_Dach und Fassade"),
  n("05_Garten und Außenbereich"),
  n("06_Geräte und Garantien", "Rechnungen", "Handbücher"),
  n("07_Versicherungen und Verträge"),
  n("08_Fotos vom Zustand"),
];

/**
 * Vorlagen für den Start. Bewusst breit: Aufträge aller Gewerke, Wartung und Prüfung
 * sowie Nachweise im Arbeitsschutz (Unterweisungen, Belehrungen, Gefährdungsbeurteilungen).
 */
export const BUILTIN_TEMPLATES: TemplateInfo[] = [
  {
    id: "builtin:hausakte",
    name: "Hausakte (Zuhause)",
    description: "Für Eigenheim und Wohnung: Elektro, Heizung, Wasser, Dach, Garten, Garantien und Verträge.",
    builtin: true,
    structure: HAUSAKTE_STRUCTURE,
  },
  {
    id: "builtin:auftrag",
    name: "Auftrag allgemein",
    description: "Für jeden Auftrag: Angebot bis Rechnung, Fotos vorher und nachher.",
    builtin: true,
    structure: [
      n("01_Angebot und Auftrag"),
      n("02_Pläne und Unterlagen"),
      n("03_Fotos", "Vorher", "Während", "Nachher"),
      n("04_Absprachen"),
      n("05_Abnahme"),
      n("06_Rechnung"),
    ],
  },
  {
    id: "builtin:elektro",
    name: "Elektroinstallation",
    description: "Installation mit Verteilungen, Messungen und Prüfprotokollen.",
    builtin: true,
    structure: [
      n("01_Angebot und Auftrag"),
      n("02_Pläne und Schaltpläne"),
      n("03_Fotos", "Verteilungen", "Leitungswege", "Zählerschrank"),
      n("04_Messungen und Prüfprotokolle"),
      n("05_Absprachen"),
      n("06_Abnahme und Übergabe"),
      n("07_Rechnung"),
    ],
  },
  {
    id: "builtin:wartung",
    name: "Wartung und Prüfung",
    description: "Wiederkehrende Prüfungen von Anlagen und Geräten mit Mängeln und Nachprüfung.",
    builtin: true,
    structure: [
      n("01_Anlagen und Geräte"),
      n("02_Prüfprotokolle"),
      n("03_Fotos"),
      n("04_Mängel und Nachprüfung"),
      n("05_Berichte an den Kunden"),
    ],
  },
  {
    id: "builtin:arbeitsschutz",
    name: "Arbeitsschutz im Betrieb",
    description: "Unterweisungen, Belehrungen und Gefährdungsbeurteilungen an einem Ort.",
    builtin: true,
    structure: [
      n("01_Unterweisungen", "Jährliche Unterweisung", "Neue Mitarbeiter", "Unterschriftenlisten"),
      n("02_Belehrungen"),
      n("03_Gefährdungsbeurteilungen", "Betrieb", "Baustellen"),
      n("04_Betriebsanweisungen"),
      n("05_Erste Hilfe und Brandschutz"),
      n("06_Prüfung Arbeitsmittel", "Leitern und Tritte", "Elektrische Geräte", "Fahrzeuge"),
      n("07_Persönliche Schutzausrüstung"),
    ],
  },
  {
    id: "builtin:leer",
    name: "Leer",
    description: "Ohne Ordner beginnen und die Struktur selbst anlegen.",
    builtin: true,
    structure: [],
  },
];

export type FileKind = "photo" | "pdf" | "document" | "other";

/** Erlaubte Dateien. Der Typ wird aus der Endung bestimmt, nicht aus der Angabe des Browsers. */
export const ALLOWED_FILES: Record<string, { mime: string; kind: FileKind }> = {
  jpg: { mime: "image/jpeg", kind: "photo" },
  jpeg: { mime: "image/jpeg", kind: "photo" },
  png: { mime: "image/png", kind: "photo" },
  webp: { mime: "image/webp", kind: "photo" },
  heic: { mime: "image/heic", kind: "photo" },
  pdf: { mime: "application/pdf", kind: "pdf" },
  txt: { mime: "text/plain", kind: "document" },
  docx: { mime: "application/vnd.openxmlformats-officedocument.wordprocessingml.document", kind: "document" },
  xlsx: { mime: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", kind: "document" },
  doc: { mime: "application/msword", kind: "document" },
  xls: { mime: "application/vnd.ms-excel", kind: "document" },
  msg: { mime: "application/vnd.ms-outlook", kind: "document" },
  eml: { mime: "message/rfc822", kind: "document" },
  dwg: { mime: "application/acad", kind: "other" },
  dxf: { mime: "application/dxf", kind: "other" },
  zip: { mime: "application/zip", kind: "other" },
};

export const MAX_FILE_MB = 50;

export function fileExtension(name: string): string {
  const m = /\.([A-Za-z0-9]{1,8})$/.exec(name);
  return m?.[1]?.toLowerCase() ?? "";
}

export interface ProjectSummary {
  id: string;
  name: string;
  customer: string | null;
  address: string | null;
  /** Verknüpfte Kundenkarte (Angebote und Rechnungen); das Textfeld „customer“ bleibt daneben bestehen. */
  customerId: string | null;
  status: "active" | "archived";
  fileCount: number;
  updatedAt: string;
  /** Nur zugeordnete Mitarbeiter sehen das Projekt (Inhaber und Admins immer). */
  restricted: boolean;
}

export interface FolderInfo {
  id: string;
  parentId: string | null;
  name: string;
  position: number;
  fileCount: number;
}

export interface ProjectDetail extends ProjectSummary {
  folders: FolderInfo[];
  /** Zugeordnete Mitarbeiter (nur für Inhaber und Admins gefüllt). */
  memberIds: string[];
}

export interface FileInfo {
  id: string;
  folderId: string;
  name: string;
  kind: FileKind;
  mime: string;
  size: number;
  hasThumb: boolean;
  createdAt: string;
  createdBy: string | null;
}

const name = (max: number, msg: string) =>
  z
    .string()
    .trim()
    .min(1, msg)
    .max(max, `Bitte höchstens ${max} Zeichen.`)
    .refine((v) => !/[\\/:*?"<>|]/.test(v), "Diese Zeichen sind im Namen nicht erlaubt: \\ / : * ? \" < > |");

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v ? v : undefined));

export const templateNodeSchema: z.ZodType<TemplateNode> = z.lazy(() =>
  z.object({ name: name(120, "Jeder Ordner braucht einen Namen."), children: z.array(templateNodeSchema).max(100).optional() }),
);

export const projectCreateSchema = z.object({
  name: name(120, "Bitte geben Sie einen Projektnamen an."),
  customer: optionalText(120),
  address: optionalText(200),
  customerId: z.uuid().optional(),
  templateId: z.string().max(80).optional(),
});

export const projectUpdateSchema = z.object({
  name: name(120, "Bitte geben Sie einen Projektnamen an.").optional(),
  customer: z.string().trim().max(120).nullable().optional(),
  address: z.string().trim().max(200).nullable().optional(),
  customerId: z.uuid().nullable().optional(),
  status: z.enum(["active", "archived"]).optional(),
});

export const folderCreateSchema = z.object({
  name: name(120, "Bitte geben Sie einen Ordnernamen an."),
  parentId: z.uuid().nullable().optional(),
});

export const folderUpdateSchema = z.object({
  name: name(120, "Bitte geben Sie einen Ordnernamen an.").optional(),
  parentId: z.uuid().nullable().optional(),
  position: z.number().int().min(0).max(100_000).optional(),
});

export const fileUpdateSchema = z.object({
  name: name(200, "Bitte geben Sie einen Dateinamen an.").optional(),
  folderId: z.uuid().optional(),
});

export const templateCreateSchema = z.object({
  name: name(80, "Bitte geben Sie der Vorlage einen Namen."),
  description: optionalText(200),
  /** Entweder eine Struktur angeben oder die eines bestehenden Projekts übernehmen. */
  structure: z.array(templateNodeSchema).max(100).optional(),
  fromProjectId: z.uuid().optional(),
});

/** Zählt alle Ordner einer Vorlage, um übergroße Strukturen abzulehnen. */
export function countNodes(nodes: TemplateNode[]): number {
  return nodes.reduce((sum, x) => sum + 1 + countNodes(x.children ?? []), 0);
}
export const MAX_TEMPLATE_FOLDERS = 300;

export const projectAccessSchema = z.object({
  restricted: z.boolean(),
  memberIds: z.array(z.string().uuid()).max(500),
});
